import { FooterV2Props, FooterV2SocialLink } from "@/shared/data/home.v2.data";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faFacebookF,
  faInstagram,
  faLinkedinIn,
  faXTwitter,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * FooterV2 — global footer for the new design system.
 *
 * Two-column layout (description + social left, link groups right) with
 * a giant faded "GRAPHY" wordmark bleeding off the bottom as decoration.
 */

const socialIconMap: Record<FooterV2SocialLink["kind"], IconDefinition> = {
  youtube: faYoutube,
  instagram: faInstagram,
  linkedin: faLinkedinIn,
  facebook: faFacebookF,
  twitter: faXTwitter,
  mail: faEnvelope,
};

const FooterV2 = ({
  description,
  socialLinks,
  copyright,
  linkGroups,
  wordmark,
  wordmarkImage,
}: FooterV2Props) => {
  return (
    <footer className="relative overflow-hidden bg-black text-white">
      <div className="container relative z-10 mx-auto px-4 pb-12 pt-10 sm:px-6 md:px-8 md:pb-16 md:pt-20 lg:px-12 lg:pb-20">
        <div className="mx-auto grid max-w-6xl gap-12 md:grid-cols-12 md:gap-8">
          {/* Left: brand + description + socials + copyright */}
          <div className="md:col-span-6">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/icons/graphy-logo.svg"
              alt={wordmark}
              className="block h-7 w-auto md:h-8"
            />
            <p className="mt-6 max-w-md font-inter text-[14px] leading-relaxed text-darkSlate03">
              {description}
            </p>
            <ul className="mt-8 flex items-center gap-5">
              {socialLinks.map((s) => (
                <li key={s.kind}>
                  <a
                    href={s.href}
                    aria-label={s.label}
                    className="inline-flex h-8 w-8 items-center justify-center text-white transition-colors hover:text-brandSecondary"
                    target={s.kind === "mail" ? "_self" : "_blank"}
                    rel="noopener noreferrer"
                  >
                    <FontAwesomeIcon
                      icon={socialIconMap[s.kind]}
                      style={{ fontSize: 18 }}
                    />
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-10 font-inter text-[13px] text-darkSlate03">
              {copyright}
            </div>
          </div>

          {/* Right: link groups — each heading gets its own column. */}
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 md:col-span-6 md:gap-8">
            {linkGroups.map((group) => (
              <div key={group.heading}>
                <div className="font-inter text-[12px] font-semibold uppercase tracking-wider text-white">
                  {group.heading}
                </div>
                <ul className="mt-5 space-y-4">
                  {group.links.map((l) => (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        className="font-inter text-[14px] text-darkSlate03 transition-colors hover:text-white"
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Giant faded GRAPHY wordmark — the SVG sits inside the same
          `container mx-auto` + horizontal padding ramp as the content
          above so its left/right edges align with the logo/description
          and link groups. If the asset isn't present yet, the slot still
          reserves vertical space so the layout matches the final design. */}
      <div className="container relative mx-auto w-full px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="mx-auto max-w-6xl">
        {wordmarkImage ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={wordmarkImage}
            alt=""
            aria-hidden="true"
            className="block w-full select-none"
          />
        ) : (
          // Placeholder: reserves roughly the same vertical space as the
          // final SVG so layout doesn't shift when the asset is dropped in.
          <div
            aria-hidden="true"
            className="block w-full select-none"
            style={{ aspectRatio: "1600 / 280" }}
          >
            <span className="sr-only">{wordmark}</span>
          </div>
        )}
        </div>
      </div>
    </footer>
  );
};

export default FooterV2;
